import { config, getApiOrigin, getCheckoutOrigin, isOriginAllowed } from '../config';
import type {
  PaypercutElements,
  PaypercutElementsAppearance,
  PaypercutElementsOptions,
  PaypercutElementsUpdateOptions,
  PaypercutOptions,
  ExpressCheckoutElement,
  ExpressCheckoutElementUpdateOptions,
  ExpressCheckoutElementOptions,
  ExpressCheckoutShippingUpdate,
  ExpressCheckoutWallet,
  CardCvcElement,
  CardExpiryElement,
  CardFieldElementType,
  CardNumberElementOptions,
  CardNumberElement,
  DeferredPaymentReference,
  ElementsErrorCode,
  ElementsSubmitError,
  CardElement,
  PaypercutBillingDetails,
} from '../types/elements';
import {
  ELEMENTS_COMPONENT_CHANNEL,
  ELEMENTS_CONTROLLER_CHANNEL,
  ElementsControllerCommandType,
  ElementsControllerMessageType,
  type ElementsComponentEventMessage,
  type ElementsControllerApplyDeveloperPresetMessage,
  type ElementsControllerApplePayAuthorizedMessage,
  type ElementsControllerApplePayCanceledMessage,
  type ElementsControllerApplePayCapabilitiesMessage,
  type ElementsControllerApplePayValidateMerchantMessage,
  type ElementsControllerGooglePayAuthorizedMessage,
  type ElementsControllerGooglePayCanceledMessage,
  type ElementsControllerGooglePayCapabilitiesMessage,
  type ElementsControllerGooglePayClickMessage,
  type ElementsControllerGooglePayDataChangedMessage,
  type ElementsControllerGooglePayErrorMessage,
  type ElementsControllerGooglePayPaymentDataMessage,
  type ElementsControllerResolveWalletClickMessage,
  type ElementsControllerResolveWalletConfirmationMessage,
  type ElementsControllerResolveWalletUpdateMessage,
  type ElementsControllerAttachActionChannelMessage,
  type ElementsControllerAttachComponentMessage,
  type ElementsControllerCancelComponentSubmissionMessage,
  type ElementsControllerCardCollectionTokenizationResultMessage,
  type ElementsControllerDiscoverDeveloperPresetsMessage,
  type ElementsControllerDetachComponentMessage,
  type ElementsControllerInitMessage,
  type ElementsControllerOptions,
  type ElementsControllerSubmitComponentMessage,
  type ElementsControllerValidateComponentMessage,
  type ElementsSubmissionCancellationReason,
  type ElementsControllerUpdateOptionsMessage,
  type ElementsControllerUpdateComponentOptionsMessage,
  type CardFieldComponentType,
  isElementsComponentEventMessage,
  isElementsControllerErrorMessage,
  isElementsControllerFrameReadyMessage,
  isElementsControllerInitializedMessage,
} from './controller-protocol';
import { CardElementImpl } from './card-element';
import { ExpressCheckoutElementImpl } from './express-checkout-element';
import { ElementsWalletAttemptOverlay } from './wallet-attempt-overlay';
import { buildElementsControllerFrameUrl, withElementsFrameNavigationIdentity } from './frame-url';
import {
  assertValidElementsAppearance,
  cloneElementsAppearance,
  elementsAppearanceEqual,
} from './appearance';
import { generateId } from '../utils/generate-id';
import { containsAsciiControlCharacter, isRecord } from './protocol-validation';
import { CardFieldElementImpl } from './card-field-element';
import {
  registerDeveloperAssistantTarget,
  type DeveloperAssistantRegistration,
  type DeveloperAssistantPreparedAppearance,
} from './developer-assistant-registration';
import type { PreparedAppearanceTransition } from './appearance-transition-frame';
import {
  CardCollectionRuntime,
  type MountCardCollectionFieldInput,
} from './card-collection-runtime';
import type { CardCollectionFieldPresentation } from './card-collection-provider';
import type { CardFieldCopy } from './card-field-copy';
import { createBasisTheoryCardFieldStyle } from './card-field-appearance';
import { createSubmitError, PaymentMethodSubmissionCoordinator } from './payment-method-submission';
import {
  CardCollectionSubmissionSource,
  type CardCollectionSubmissionContext,
} from './card-collection-submission-source';

type ElementsComponentImpl = CardElementImpl | ExpressCheckoutElementImpl | CardFieldElementImpl;

export interface ElementsGroupUpdateSnapshot {
  readonly updateRevision: number;
  readonly options: ElementsControllerOptions;
}

export interface ElementsGroupContext {
  readonly controllerId: string;
  readonly configurationIdentity: string;
  readonly hostedOrigin: string;
  getClientOptions(): PaypercutOptions;
  getElementsOptions(): PaypercutElementsOptions;
  getRenderableElementsOptions(): PaypercutElementsOptions;
  markComponentMounted(): void;
  ensureController(): void;
  attachComponent(element: ElementsComponentImpl, port: MessagePort): void;
  reattachComponent(element: ElementsComponentImpl, port: MessagePort): void;
  submitComponent(
    componentId: string,
    requestId: string,
    deadlineAt: number,
    billingDetails?: PaypercutBillingDetails,
  ): void;
  validateComponent(componentId: string, requestId: string): void;
  invalidateSubmissionValidation(): void;
  cancelComponentSubmission(
    componentId: string,
    requestId: string,
    reason?: ElementsSubmissionCancellationReason,
  ): void;
  beginWalletAttempt(
    componentId: string,
    requestId: string,
    wallet: ExpressCheckoutWallet,
    providerElement?: HTMLElement | null,
  ): boolean;
  hideWalletAttemptOverlay(componentId: string, requestId: string): void;
  markWalletAttemptProcessing(
    componentId: string,
    requestId: string,
    wallet: ExpressCheckoutWallet,
  ): void;
  endWalletAttempt(componentId: string, requestId: string): void;
  attachActionChannel(
    componentId: string,
    requestId: string,
    actionId: string,
    port: MessagePort,
  ): void;
  hasPaymentMethodSubmissionState(sourceId: string): boolean;
  hasCompletedPaymentMethodSubmission(sourceId: string): boolean;
  sendApplePayCapabilities(componentId: string, available: boolean): void;
  sendApplePayValidateMerchant(componentId: string, requestId: string, validationUrl: string): void;
  sendApplePayAuthorized(
    componentId: string,
    requestId: string,
    payment: Record<string, unknown>,
  ): void;
  sendApplePayCanceled(componentId: string, requestId: string): void;
  sendGooglePayCapabilities(componentId: string, available: boolean): void;
  sendGooglePayClick(componentId: string, requestId: string): void;
  sendGooglePayDataChanged(
    componentId: string,
    requestId: string,
    updateId: string,
    dataJson: string,
  ): void;
  sendGooglePayAuthorized(componentId: string, requestId: string, dataJson: string): void;
  sendGooglePayPaymentData(componentId: string, requestId: string, dataJson: string): void;
  sendGooglePayCanceled(componentId: string, requestId: string): void;
  sendGooglePayError(componentId: string, requestId: string, code: string, message: string): void;
  resolveWalletClick(
    componentId: string,
    requestId: string,
    accepted: boolean,
    options?: ExpressCheckoutElementUpdateOptions,
    amount?: number,
  ): void;
  resolveWalletUpdate(
    componentId: string,
    requestId: string,
    updateId: string,
    accepted: boolean,
    update?: ExpressCheckoutShippingUpdate,
  ): void;
  resolveWalletConfirmation(
    componentId: string,
    requestId: string,
    succeeded: boolean,
    error?: { code?: string; message?: string },
  ): void;
  updateExpressCheckoutComponent(
    componentId: string,
    componentRevision: number,
    componentOptions: ExpressCheckoutElementOptions,
  ): void;
  discoverDeveloperPresets(componentId: string): void;
  applyDeveloperPreset(componentId: string, requestId: string, presetId: string): void;
  getUpdateSnapshot(): ElementsGroupUpdateSnapshot;
  getAppearance(): PaypercutElementsAppearance & {
    theme: 'light' | 'dark';
    inputs: 'condensed' | 'spaced';
    labels: 'auto' | 'above' | 'floating';
  };
  updateAppearance(appearance: PaypercutElementsAppearance): Promise<void>;
  replaceAppearance(appearance: PaypercutElementsAppearance): Promise<void>;
  prepareAppearance(
    appearance: PaypercutElementsAppearance,
  ): Promise<DeveloperAssistantPreparedAppearance>;
  prepareAppearanceReplacement(
    appearance: PaypercutElementsAppearance,
  ): Promise<DeveloperAssistantPreparedAppearance>;
  isAppearancePreviewAvailable(): boolean;
  commandCardField(
    componentId: string,
    componentType: CardFieldComponentType,
    action: 'clear' | 'focus' | 'blur',
  ): void;
  mountCardField(input: MountCardCollectionFieldInput): Promise<void>;
  resolveCardFieldCopy(type: CardFieldElementType): Promise<CardFieldCopy>;
  updateCardFieldPresentation(
    componentId: string,
    presentation: CardCollectionFieldPresentation,
  ): void;
  onCardFieldAvailabilityChange(): void;
  onCardFieldValueChange(): void;
  destroyCardField(componentId: string): void;
  detachComponent(element: ElementsComponentImpl): void;
  isDestroyed(): boolean;
  remove(element: ElementsComponentImpl): void;
}

type PendingComponent = { element: ElementsComponentImpl; port: MessagePort };

type AppearancePreview = {
  readonly generation: number;
  readonly appearance: ElementsControllerOptions['appearance'];
};

type AppearancePreviewErrorCode =
  | 'elements_appearance_preview_refused'
  | 'elements_appearance_preview_failed'
  | 'elements_appearance_preview_superseded';

class AppearancePreviewError extends Error {
  constructor(
    readonly code: AppearancePreviewErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.name = 'AppearancePreviewError';
    if (cause !== undefined) (this as Error & { cause?: unknown }).cause = cause;
  }
}

const createElementsError = (
  code: ElementsErrorCode,
  message: string,
  recoverable: boolean,
): ElementsSubmitError => Object.assign(new Error(message), { code, recoverable });

const normalizeCardNumberElementOptions = (value: unknown): CardNumberElementOptions => {
  if (value === undefined) return {};
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw createElementsError(
      'elements_component_configuration_invalid',
      'Card number Element options must be an object.',
      true,
    );
  }
  const options = value as Record<string, unknown>;
  if (Object.keys(options).some((key) => key !== 'showIcon')) {
    throw createElementsError(
      'elements_component_configuration_invalid',
      'Card number Element options contain an unsupported property.',
      true,
    );
  }
  if (options.showIcon !== undefined && typeof options.showIcon !== 'boolean') {
    throw createElementsError(
      'elements_component_configuration_invalid',
      'Card number Element showIcon must be a boolean.',
      true,
    );
  }
  return options.showIcon === undefined ? {} : { showIcon: options.showIcon };
};

export class ElementsGroupImpl
  implements PaypercutElements, ElementsGroupContext, CardCollectionSubmissionContext
{
  readonly controllerId = generateId();
  readonly hostedOrigin: string;
  private readonly elements = new Map<string, ElementsComponentImpl>();
  private readonly cardFields = new Map<CardFieldElementType, CardFieldElementImpl>();
  private readonly cardCollectionRuntime: CardCollectionRuntime;
  private readonly cardCollectionSubmissionSource: CardCollectionSubmissionSource;
  private readonly paymentMethodSubmission: PaymentMethodSubmissionCoordinator;
  private readonly pendingComponents = new Map<string, PendingComponent>();
  private controllerIframe: HTMLIFrameElement | null = null;
  private controlPort: MessagePort | null = null;
  private controllerInitialized = false;
  private controllerBootstrapped = false;
  private destroyed = false;
  private appearance: ElementsControllerOptions['appearance'];
  private appearancePreview: AppearancePreview | null = null;
  private appearancePreviewGeneration = 0;
  private appearanceLocked = false;
  private warnedAboutSkippedAppearance = false;
  private amount: number;
  private updateRevision = 0;
  private validationRevision = 0;
  private validatedSource: { sourceId: string; revision: number } | null = null;
  private pendingValidation: {
    sourceId: string;
    revision: number;
    promise: Promise<void>;
  } | null = null;
  private readonly walletAttemptOverlay = new ElementsWalletAttemptOverlay();
  private splitCardDeveloperAssistant: DeveloperAssistantRegistration | null = null;
  private readonly windowMessageHandler = (event: MessageEvent): void =>
    this.onWindowMessage(event);

  constructor(
    private readonly clientOptions: PaypercutOptions,
    private readonly elementsOptions: PaypercutElementsOptions,
    readonly configurationIdentity: string,
  ) {
    this.hostedOrigin = resolveHostedOrigin(clientOptions.hostedCheckoutUrl);
    this.amount = elementsOptions.amount;
    if (elementsOptions.appearance) {
      assertValidElementsAppearance(elementsOptions.appearance);
    }
    this.appearance = cloneElementsAppearance(elementsOptions.appearance);
    this.cardCollectionRuntime = new CardCollectionRuntime(
      this.appearance,
      createBasisTheoryCardFieldStyle,
    );
    this.cardCollectionSubmissionSource = new CardCollectionSubmissionSource(
      this.cardCollectionRuntime,
      this,
    );
    this.paymentMethodSubmission = new PaymentMethodSubmissionCoordinator(this.hostedOrigin);
    if (!isOriginAllowed(this.hostedOrigin)) {
      throw new Error(
        `[Paypercut Elements] Hosted checkout origin is not allowed: ${this.hostedOrigin}`,
      );
    }
  }

  create(type: 'card'): CardElement;
  create(type: 'expressCheckout', options?: ExpressCheckoutElementOptions): ExpressCheckoutElement;
  create(type: 'cardNumber', options?: CardNumberElementOptions): CardNumberElement;
  create(type: 'cardExpiry'): CardExpiryElement;
  create(type: 'cardCvc'): CardCvcElement;
  create(
    type: 'card' | 'expressCheckout' | CardFieldElementType,
    options?: ExpressCheckoutElementOptions | CardNumberElementOptions,
  ): CardElement | ExpressCheckoutElement | CardNumberElement | CardExpiryElement | CardCvcElement {
    if (this.destroyed) {
      throw new Error('[Paypercut Elements] Cannot create an Element from a destroyed group.');
    }
    if (type === 'card') {
      if (this.cardFields.size > 0) {
        throw createElementsError(
          'elements_component_conflict',
          'A Card Element and split card fields cannot share one Elements group.',
          false,
        );
      }
      if (Array.from(this.elements.values()).some((element) => element.componentType === 'card')) {
        throw createElementsError(
          'elements_component_conflict',
          'Only one Card Element may be created in an Elements group.',
          true,
        );
      }
      const element = new CardElementImpl(this);
      this.elements.set(element.componentId, element);
      return element;
    }
    if (type === 'expressCheckout') {
      const element = new ExpressCheckoutElementImpl(
        this,
        options as ExpressCheckoutElementOptions | undefined,
      );
      this.elements.set(element.componentId, element);
      return element;
    }
    if (type === 'cardNumber' || type === 'cardExpiry' || type === 'cardCvc') {
      if (type !== 'cardNumber' && options !== undefined) {
        throw createElementsError(
          'elements_component_configuration_invalid',
          `${type} does not accept creation options.`,
          true,
        );
      }
      if (Array.from(this.elements.values()).some((element) => element.componentType === 'card')) {
        throw createElementsError(
          'elements_component_conflict',
          'Split card fields and a Card Element cannot share one Elements group.',
          false,
        );
      }
      if (this.cardFields.has(type)) {
        throw createElementsError(
          'elements_card_field_duplicate',
          `Only one ${type} field may be created in an Elements group.`,
          true,
        );
      }
      const element = new CardFieldElementImpl(
        type,
        this,
        type === 'cardNumber' ? normalizeCardNumberElementOptions(options) : undefined,
      );
      this.cardFields.set(type, element);
      this.elements.set(element.componentId, element);
      this.ensureSplitCardDeveloperAssistant();
      return element as CardNumberElement | CardExpiryElement | CardCvcElement;
    }
    throw new Error(`[Paypercut Elements] Unsupported Element type: ${String(type)}`);
  }

  submit(): Promise<void> {
    if (this.appearancePreview) return Promise.reject(this.appearancePreviewNotReadyError());
    let source: CardElementImpl | CardCollectionSubmissionSource;
    try {
      source = this.resolvePaymentMethodSource();
    } catch (error) {
      return Promise.reject(error);
    }
    const submissionBlocker = this.paymentMethodSubmission.getIndeterminateAttemptError();
    if (submissionBlocker) return Promise.reject(submissionBlocker);
    const revision = this.validationRevision;
    if (
      this.validatedSource?.sourceId === source.submissionSourceId &&
      this.validatedSource.revision === revision
    ) {
      return Promise.resolve();
    }
    if (
      this.pendingValidation?.sourceId === source.submissionSourceId &&
      this.pendingValidation.revision === revision
    ) {
      return this.pendingValidation.promise;
    }
    const promise = source.validatePaymentMethod().then(() => {
      if (this.destroyed) {
        throw createSubmitError(
          'elements_destroyed',
          'The Elements group has been destroyed.',
          false,
        );
      }
      if (revision !== this.validationRevision) {
        throw createSubmitError(
          'elements_submit_required',
          'Payment details changed while they were being validated. Submit the Elements group again.',
          true,
        );
      }
      this.validatedSource = { sourceId: source.submissionSourceId, revision };
    });
    this.pendingValidation = { sourceId: source.submissionSourceId, revision, promise };
    void promise.then(
      () => {
        if (this.pendingValidation?.promise === promise) this.pendingValidation = null;
      },
      () => {
        if (this.pendingValidation?.promise === promise) this.pendingValidation = null;
      },
    );
    return promise;
  }

  createPaymentMethod(billingDetails?: PaypercutBillingDetails): Promise<DeferredPaymentReference> {
    if (this.appearancePreview) return Promise.reject(this.appearancePreviewNotReadyError());
    let source: CardElementImpl | CardCollectionSubmissionSource;
    try {
      source = this.resolvePaymentMethodSource();
    } catch (error) {
      return Promise.reject(error);
    }
    const submissionBlocker = this.paymentMethodSubmission.getIndeterminateAttemptError();
    if (submissionBlocker) return Promise.reject(submissionBlocker);
    if (
      this.validatedSource?.sourceId !== source.submissionSourceId ||
      this.validatedSource.revision !== this.validationRevision
    ) {
      try {
        source.assertCanSubmitPaymentMethod();
      } catch (error) {
        if (isSubmitErrorWithCode(error, 'elements_not_ready')) return Promise.reject(error);
      }
      return Promise.reject(
        createSubmitError(
          'elements_submit_required',
          'Call elements.submit() after the latest payment-details change before creating a Payment Method.',
          true,
        ),
      );
    }
    return this.paymentMethodSubmission.submit(
      source,
      this.elementsOptions.setupFutureUsage,
      billingDetails,
    );
  }

  private resolvePaymentMethodSource(): CardElementImpl | CardCollectionSubmissionSource {
    if (this.destroyed) {
      throw createSubmitError(
        'elements_destroyed',
        'The Elements group has been destroyed.',
        false,
      );
    }

    const paymentSources = Array.from(this.elements.values()).filter(
      (element): element is CardElementImpl => element instanceof CardElementImpl,
    );
    if (paymentSources.length > 1) {
      throw createSubmitError(
        'elements_component_conflict',
        'More than one active payment-method source is mounted in this Elements group.',
        true,
      );
    }
    if (paymentSources.length === 1) {
      return paymentSources[0];
    }
    if (this.cardFields.size > 0) {
      return this.cardCollectionSubmissionSource;
    }
    throw createSubmitError(
      'elements_not_mounted',
      'Create and mount a payment-method Element before submitting.',
      true,
    );
  }

  getClientOptions(): PaypercutOptions {
    return { ...this.clientOptions };
  }

  getElementsOptions(): PaypercutElementsOptions {
    return this.serializeElementsOptions(this.appearance);
  }

  getRenderableElementsOptions(): PaypercutElementsOptions {
    return this.serializeElementsOptions(this.appearancePreview?.appearance ?? this.appearance);
  }

  private serializeElementsOptions(
    appearance: ElementsControllerOptions['appearance'],
  ): PaypercutElementsOptions {
    return {
      ...this.elementsOptions,
      amount: this.amount,
      ...(appearance ? { appearance: cloneElementsAppearance(appearance) } : {}),
    };
  }

  markComponentMounted(): void {
    this.appearanceLocked = true;
  }

  ensureController(): void {
    if (this.destroyed || this.controllerIframe) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('[Paypercut Elements] Elements can only be mounted in a browser.');
    }

    const iframe = document.createElement('iframe');
    iframe.id = `paypercut-elements-controller-${this.controllerId}`;
    iframe.title = 'Paypercut payment controller';
    iframe.referrerPolicy = 'origin';
    iframe.src = withElementsFrameNavigationIdentity(
      buildElementsControllerFrameUrl({
        hostedOrigin: this.hostedOrigin,
        controllerId: this.controllerId,
        clientOptions: this.clientOptions,
        elementsOptions: this.getElementsOptions(),
        sdkVersion: config.version,
        referrer: window.location.origin,
      }),
      generateId(),
    );
    iframe.allow = 'payment *';
    iframe.setAttribute('allowtransparency', 'true');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('role', 'presentation');
    iframe.setAttribute('scrolling', 'no');
    iframe.tabIndex = -1;
    Object.assign(iframe.style, {
      position: 'fixed',
      width: '0',
      height: '0',
      minWidth: '0',
      margin: '0',
      padding: '0',
      border: '0',
      display: 'block',
      overflow: 'hidden',
      opacity: '0',
      pointerEvents: 'none',
      userSelect: 'none',
      visibility: 'hidden',
    });

    this.controllerIframe = iframe;
    window.addEventListener('message', this.windowMessageHandler);
    (document.body ?? document.documentElement).appendChild(iframe);
  }

  attachComponent(element: ElementsComponentImpl, port: MessagePort): void {
    if (this.destroyed || !this.elements.has(element.componentId)) {
      port.close();
      return;
    }
    this.pendingComponents.set(element.componentId, { element, port });
    this.ensureController();
    this.flushPendingComponents();
  }

  reattachComponent(element: ElementsComponentImpl, port: MessagePort): void {
    if (this.destroyed || !this.elements.has(element.componentId)) {
      port.close();
      return;
    }

    this.invalidateSubmissionValidation();
    this.paymentMethodSubmission.reinitializeSource(
      element instanceof CardElementImpl ? element.submissionSourceId : element.componentId,
    );
    this.detachComponent(element);
    this.pendingComponents.set(element.componentId, { element, port });
    this.flushPendingComponents();
  }

  submitComponent(
    componentId: string,
    requestId: string,
    deadlineAt: number,
    billingDetails?: PaypercutBillingDetails,
  ): void {
    const message: ElementsControllerSubmitComponentMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.SubmitComponent,
      controllerId: this.controllerId,
      componentId,
      componentType: 'card',
      requestId,
      deadlineAt,
      ...(billingDetails ? { billingDetails } : {}),
    };
    this.postControl(message);
  }

  validateComponent(componentId: string, requestId: string): void {
    const message: ElementsControllerValidateComponentMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ValidateComponent,
      controllerId: this.controllerId,
      componentId,
      componentType: 'card',
      requestId,
    };
    this.postControl(message);
  }

  invalidateSubmissionValidation(): void {
    this.validatedSource = null;
    if (this.validationRevision < Number.MAX_SAFE_INTEGER) this.validationRevision += 1;
  }

  submitCardCollection(
    componentId: string,
    requestId: string,
    deadlineAt: number,
    billingDetails?: PaypercutBillingDetails,
  ): void {
    const message: ElementsControllerSubmitComponentMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.SubmitComponent,
      controllerId: this.controllerId,
      componentId,
      componentType: 'card_collection',
      requestId,
      deadlineAt,
      ...(billingDetails ? { billingDetails } : {}),
    };
    this.postControl(message);
  }

  cancelCardCollectionSubmission(
    componentId: string,
    requestId: string,
    reason: ElementsSubmissionCancellationReason = 'canceled',
  ): void {
    const message: ElementsControllerCancelComponentSubmissionMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.CancelComponentSubmission,
      controllerId: this.controllerId,
      componentId,
      componentType: 'card_collection',
      requestId,
      reason,
    };
    this.postControl(message);
  }

  cancelComponentSubmission(
    componentId: string,
    requestId: string,
    reason: ElementsSubmissionCancellationReason = 'canceled',
  ): void {
    const component = this.elements.get(componentId);
    if (!component) return;
    const message: ElementsControllerCancelComponentSubmissionMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.CancelComponentSubmission,
      controllerId: this.controllerId,
      componentId,
      componentType: component.componentType,
      requestId,
      reason,
    };
    this.postControl(message);
  }

  beginWalletAttempt(
    componentId: string,
    requestId: string,
    wallet: ExpressCheckoutWallet,
    providerElement?: HTMLElement | null,
  ): boolean {
    const component = this.elements.get(componentId);
    if (!component || component.componentType !== 'express_checkout') return false;
    return this.walletAttemptOverlay.show(
      componentId,
      requestId,
      wallet,
      () => this.cancelComponentSubmission(componentId, requestId),
      providerElement,
    );
  }

  hideWalletAttemptOverlay(componentId: string, requestId: string): void {
    this.walletAttemptOverlay.hide(componentId, requestId);
  }

  markWalletAttemptProcessing(
    componentId: string,
    requestId: string,
    wallet: ExpressCheckoutWallet,
  ): void {
    this.walletAttemptOverlay.setPhase(componentId, requestId, wallet, 'processing');
  }

  endWalletAttempt(componentId: string, requestId: string): void {
    this.walletAttemptOverlay.close(componentId, requestId);
  }

  attachActionChannel(
    componentId: string,
    requestId: string,
    actionId: string,
    port: MessagePort,
  ): void {
    const component = this.elements.get(componentId);
    if (!component) {
      port.close();
      return;
    }
    const message: ElementsControllerAttachActionChannelMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.AttachActionChannel,
      controllerId: this.controllerId,
      componentId,
      componentType: component.componentType,
      requestId,
      actionId,
    };
    this.postControl(message, [port]);
  }

  attachCardCollectionActionChannel(
    componentId: string,
    requestId: string,
    actionId: string,
    port: MessagePort,
  ): void {
    const message: ElementsControllerAttachActionChannelMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.AttachActionChannel,
      controllerId: this.controllerId,
      componentId,
      componentType: 'card_collection',
      requestId,
      actionId,
    };
    this.postControl(message, [port]);
  }

  sendCardCollectionTokenizationResult(
    message: ElementsControllerCardCollectionTokenizationResultMessage,
  ): void {
    this.postControl(message);
  }

  revealCardFieldSubmissionError(
    type: CardFieldElementType,
    errorType: 'incomplete' | 'invalid',
  ): void {
    this.cardFields.get(type)?.revealSubmissionError(errorType);
  }

  onCardCollectionSubmissionStateChange(): void {
    this.splitCardDeveloperAssistant?.collapse();
    this.splitCardDeveloperAssistant?.refresh();
  }

  hasPaymentMethodSubmissionState(sourceId: string): boolean {
    return this.paymentMethodSubmission.hasActiveSubmission(sourceId);
  }

  hasCompletedPaymentMethodSubmission(sourceId: string): boolean {
    return this.paymentMethodSubmission.hasCompletedSubmission(sourceId);
  }

  sendApplePayCapabilities(componentId: string, available: boolean): void {
    const message: ElementsControllerApplePayCapabilitiesMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ApplePayCapabilities,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      available,
    };
    this.postControl(message);
  }

  sendApplePayValidateMerchant(
    componentId: string,
    requestId: string,
    validationUrl: string,
  ): void {
    const message: ElementsControllerApplePayValidateMerchantMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ApplePayValidateMerchant,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      validationUrl,
    };
    this.postControl(message);
  }

  sendApplePayAuthorized(
    componentId: string,
    requestId: string,
    payment: Record<string, unknown>,
  ): void {
    const message: ElementsControllerApplePayAuthorizedMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ApplePayAuthorized,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      payment,
    };
    this.postControl(message);
  }

  sendApplePayCanceled(componentId: string, requestId: string): void {
    const message: ElementsControllerApplePayCanceledMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ApplePayCanceled,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
    };
    this.postControl(message);
  }

  sendGooglePayCapabilities(componentId: string, available: boolean): void {
    const message: ElementsControllerGooglePayCapabilitiesMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.GooglePayCapabilities,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      available,
    };
    this.postControl(message);
  }

  sendGooglePayClick(componentId: string, requestId: string): void {
    const message: ElementsControllerGooglePayClickMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.GooglePayClick,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
    };
    this.postControl(message);
  }

  sendGooglePayDataChanged(
    componentId: string,
    requestId: string,
    updateId: string,
    dataJson: string,
  ): void {
    const message: ElementsControllerGooglePayDataChangedMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.GooglePayDataChanged,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      updateId,
      dataJson,
    };
    this.postControl(message);
  }

  sendGooglePayAuthorized(componentId: string, requestId: string, dataJson: string): void {
    const message: ElementsControllerGooglePayAuthorizedMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.GooglePayAuthorized,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      dataJson,
    };
    this.postControl(message);
  }

  sendGooglePayPaymentData(componentId: string, requestId: string, dataJson: string): void {
    const message: ElementsControllerGooglePayPaymentDataMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.GooglePayPaymentData,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      dataJson,
    };
    this.postControl(message);
  }

  sendGooglePayCanceled(componentId: string, requestId: string): void {
    const message: ElementsControllerGooglePayCanceledMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.GooglePayCanceled,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
    };
    this.postControl(message);
  }

  sendGooglePayError(componentId: string, requestId: string, code: string, message: string): void {
    const control: ElementsControllerGooglePayErrorMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.GooglePayError,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      code,
      message,
    };
    this.postControl(control);
  }

  resolveWalletClick(
    componentId: string,
    requestId: string,
    accepted: boolean,
    options?: ExpressCheckoutElementUpdateOptions,
    amount?: number,
  ): void {
    if (accepted && amount !== undefined) this.commitAmountUpdate(amount);
    const snapshot = this.getUpdateSnapshot().options;
    const message: ElementsControllerResolveWalletClickMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ResolveWalletClick,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      accepted,
      ...(options ? { options } : {}),
      amount: snapshot.amount,
      currency: snapshot.currency,
    };
    this.postControl(message);
  }

  resolveWalletUpdate(
    componentId: string,
    requestId: string,
    updateId: string,
    accepted: boolean,
    update?: ExpressCheckoutShippingUpdate,
  ): void {
    const snapshot = this.getUpdateSnapshot().options;
    const { amount: resolvedAmount = snapshot.amount, ...shippingDetails } = update ?? {};
    const hasShippingDetails = Object.keys(shippingDetails).length > 0;
    const message: ElementsControllerResolveWalletUpdateMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ResolveWalletUpdate,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      updateId,
      accepted,
      ...(hasShippingDetails ? { update: shippingDetails } : {}),
      amount: resolvedAmount,
      currency: snapshot.currency,
    };
    this.postControl(message);
  }

  resolveWalletConfirmation(
    componentId: string,
    requestId: string,
    succeeded: boolean,
    error?: { code?: string; message?: string },
  ): void {
    const message: ElementsControllerResolveWalletConfirmationMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ResolveWalletConfirmation,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      requestId,
      succeeded,
      ...(error ? { error } : {}),
    };
    this.postControl(message);
  }

  updateExpressCheckoutComponent(
    componentId: string,
    componentRevision: number,
    componentOptions: ExpressCheckoutElementOptions,
  ): void {
    const component = this.elements.get(componentId);
    if (!component || component.componentType !== 'express_checkout') {
      return;
    }
    // A component updated before the controller is ready will be attached with
    // its latest full snapshot by flushPendingComponents().
    if (!this.controllerInitialized) return;
    const message: ElementsControllerUpdateComponentOptionsMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.UpdateComponentOptions,
      controllerId: this.controllerId,
      componentId,
      componentType: 'express_checkout',
      componentRevision,
      componentOptions,
    };
    this.postControl(message);
  }

  discoverDeveloperPresets(componentId: string): void {
    if (this.clientOptions.developerAssistant !== true) return;
    const message: ElementsControllerDiscoverDeveloperPresetsMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.DiscoverDeveloperPresets,
      controllerId: this.controllerId,
      componentId,
      componentType: 'card',
    };
    this.postControl(message);
  }

  applyDeveloperPreset(componentId: string, requestId: string, presetId: string): void {
    const message: ElementsControllerApplyDeveloperPresetMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.ApplyDeveloperPreset,
      controllerId: this.controllerId,
      componentId,
      componentType: 'card',
      requestId,
      presetId,
    };
    this.postControl(message);
  }

  commandCardField(
    componentId: string,
    componentType: CardFieldComponentType,
    action: 'clear' | 'focus' | 'blur',
  ): void {
    const element = this.elements.get(componentId);
    if (!element || element.componentType !== componentType) {
      throw new Error('[Paypercut Elements] The secure card field is unavailable.');
    }
    this.cardCollectionRuntime.command(componentId, action);
  }

  mountCardField(input: MountCardCollectionFieldInput): Promise<void> {
    this.ensureController();
    return this.cardCollectionRuntime.mountField(input);
  }

  resolveCardFieldCopy(type: CardFieldElementType): Promise<CardFieldCopy> {
    this.ensureController();
    return this.cardCollectionRuntime.resolveFieldCopy(type);
  }

  updateCardFieldPresentation(
    componentId: string,
    presentation: CardCollectionFieldPresentation,
  ): void {
    this.cardCollectionRuntime.updateFieldPresentation(componentId, presentation);
  }

  onCardFieldAvailabilityChange(): void {
    this.invalidateSubmissionValidation();
    this.splitCardDeveloperAssistant?.refresh();
  }

  onCardFieldValueChange(): void {
    this.invalidateSubmissionValidation();
  }

  destroyCardField(componentId: string): void {
    this.cardCollectionRuntime.destroyField(componentId);
  }

  getUpdateSnapshot(): ElementsGroupUpdateSnapshot {
    const appearance = this.appearancePreview?.appearance ?? this.appearance;
    return {
      updateRevision: this.updateRevision,
      options: toControllerOptions(this.elementsOptions, this.amount, appearance),
    };
  }

  getAppearance(): PaypercutElementsAppearance & {
    theme: 'light' | 'dark';
    inputs: 'condensed' | 'spaced';
    labels: 'auto' | 'above' | 'floating';
  } {
    return {
      theme: this.appearance?.theme ?? 'light',
      inputs: this.appearance?.inputs ?? 'condensed',
      labels: this.appearance?.labels ?? 'auto',
      ...(this.appearance?.disableAnimations !== undefined
        ? { disableAnimations: this.appearance.disableAnimations }
        : {}),
      ...(this.appearance?.variables ? { variables: { ...this.appearance.variables } } : {}),
      ...(this.appearance?.rules
        ? {
            rules: Object.fromEntries(
              Object.entries(this.appearance.rules).map(([selector, declarations]) => [
                selector,
                { ...declarations },
              ]),
            ) as NonNullable<PaypercutElementsAppearance['rules']>,
          }
        : {}),
    };
  }

  updateAppearance(appearance: PaypercutElementsAppearance): Promise<void> {
    try {
      return this.applyUpdate({ appearance }).catch((error: unknown) => {
        if (error instanceof AppearancePreviewError) this.warnAppearancePreview(error);
        throw error;
      });
    } catch (error) {
      if (error instanceof AppearancePreviewError) this.warnAppearancePreview(error);
      return Promise.reject(error);
    }
  }

  prepareAppearance(
    appearance: PaypercutElementsAppearance,
  ): Promise<DeveloperAssistantPreparedAppearance> {
    return this.prepareAppearanceCandidate(
      () => normalizeUpdateOptions({ appearance }, this.appearance, this.amount).appearance,
    );
  }

  prepareAppearanceReplacement(
    appearance: PaypercutElementsAppearance,
  ): Promise<DeveloperAssistantPreparedAppearance> {
    return this.prepareAppearanceCandidate(() => {
      assertValidElementsAppearance(appearance);
      return cloneElementsAppearance(appearance);
    });
  }

  private prepareAppearanceCandidate(
    createCandidate: () => ElementsControllerOptions['appearance'],
  ): Promise<DeveloperAssistantPreparedAppearance> {
    if (!this.isAppearancePreviewAvailable()) {
      return Promise.reject(
        new AppearancePreviewError(
          'elements_appearance_preview_refused',
          'Appearance Preview is unavailable while payment validation, Card authentication, or a wallet attempt is active.',
        ),
      );
    }
    const candidate = createCandidate();
    this.clearAppearancePreviewFailures();
    if (elementsAppearanceEqual(candidate, this.appearance)) {
      return Promise.resolve({
        isCurrent: () => true,
        commit: () => undefined,
        rollback: () => Promise.resolve(),
      });
    }
    return this.startAppearancePreview(
      candidate,
      true,
    ) as Promise<DeveloperAssistantPreparedAppearance>;
  }

  replaceAppearance(appearance: PaypercutElementsAppearance): Promise<void> {
    if (!this.isAppearancePreviewAvailable()) {
      return Promise.reject(
        new AppearancePreviewError(
          'elements_appearance_preview_refused',
          'Appearance Preview is unavailable while payment validation, Card authentication, or a wallet attempt is active.',
        ),
      );
    }
    assertValidElementsAppearance(appearance);
    const candidate = cloneElementsAppearance(appearance);
    if (elementsAppearanceEqual(candidate, this.appearance)) return Promise.resolve();
    return this.startAppearancePreview(candidate).then(() => undefined);
  }

  isAppearancePreviewAvailable(): boolean {
    return (
      !this.destroyed &&
      this.pendingValidation === null &&
      !this.paymentMethodSubmission.hasActiveSubmission() &&
      !Array.from(this.elements.values()).some((element) => element.hasActiveSubmission())
    );
  }

  update(options: PaypercutElementsUpdateOptions): void {
    try {
      void this.applyUpdate(options).catch((error: unknown) => {
        if (error instanceof AppearancePreviewError) this.warnAppearancePreview(error);
      });
    } catch (error) {
      if (error instanceof AppearancePreviewError) {
        this.warnAppearancePreview(error);
        return;
      }
      throw error;
    }
  }

  private applyUpdate(options: PaypercutElementsUpdateOptions): Promise<void> {
    if (this.destroyed) {
      throw new Error('[Paypercut Elements] Cannot update a destroyed group.');
    }
    const activeElementSubmission = Array.from(this.elements.values()).some((element) =>
      element.hasActiveSubmission(),
    );
    const activeProviderSubmission = Array.from(this.elements.values()).some((element) =>
      element.componentType === 'express_checkout'
        ? element.hasActiveProviderSubmission()
        : element.hasActiveSubmission(),
    );
    if (
      this.pendingValidation !== null ||
      this.paymentMethodSubmission.hasActiveSubmission() ||
      (options.appearance !== undefined ? activeElementSubmission : activeProviderSubmission)
    ) {
      if (options.appearance !== undefined) {
        throw new AppearancePreviewError(
          'elements_appearance_preview_refused',
          'Appearance Preview is unavailable while payment validation, Card authentication, or a wallet attempt is active.',
        );
      }
      throw createElementsError(
        'elements_submission_in_progress',
        '[Paypercut Elements] Cannot update Elements while payment details are being submitted.',
        true,
      );
    }
    const update = normalizeUpdateOptions(options, this.appearance, this.amount);
    const appearanceWasRequested = options.appearance !== undefined;
    const appearancePreviewEnabled =
      this.elementsOptions.appearanceUpdates === 'remount' ||
      this.clientOptions.developerAssistant === true;
    const hasMountedPaymentAppearanceTarget =
      this.hasMountedCardFields() ||
      this.getMountedCardElements().length > 0 ||
      this.getMountedExpressCheckoutElements().length > 0;
    const shouldRemountAppearance =
      appearanceWasRequested &&
      this.appearanceLocked &&
      appearancePreviewEnabled &&
      hasMountedPaymentAppearanceTarget;
    if (appearanceWasRequested && appearancePreviewEnabled) {
      this.clearAppearancePreviewFailures();
    }
    const appearanceIsLocked =
      appearanceWasRequested && this.appearanceLocked && !shouldRemountAppearance;
    if (appearanceIsLocked) {
      update.appearance = this.appearance;
      this.warnAboutSkippedAppearance();
    }
    const appearanceChanged = !elementsAppearanceEqual(update.appearance, this.appearance);
    if (update.amount === this.amount && !appearanceChanged) {
      return Promise.resolve();
    }
    if (!appearanceChanged) {
      this.commitAmountUpdate(update.amount);
      return Promise.resolve();
    }
    if (this.updateRevision === Number.MAX_SAFE_INTEGER) {
      throw new Error('[Paypercut Elements] The update revision limit has been reached.');
    }

    this.amount = update.amount;
    if (appearanceChanged) {
      if (shouldRemountAppearance) {
        return this.startAppearancePreview(update.appearance).then(() => undefined);
      } else {
        this.appearance = update.appearance;
        this.cardCollectionRuntime.updateAppearance(this.appearance);
        for (const field of this.cardFields.values()) field.updateAppearance(this.appearance);
      }
      this.splitCardDeveloperAssistant?.refresh();
    }
    this.invalidateSubmissionValidation();
    this.updateRevision += 1;
    if (!this.controllerInitialized) return Promise.resolve();

    const snapshot = this.getUpdateSnapshot();
    const message: ElementsControllerUpdateOptionsMessage = {
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.UpdateOptions,
      controllerId: this.controllerId,
      updateRevision: snapshot.updateRevision,
      options: snapshot.options,
    };
    this.postControl(message);
    return Promise.resolve();
  }

  private commitAmountUpdate(amount: number): void {
    if (amount === this.amount) return;
    if (this.updateRevision === Number.MAX_SAFE_INTEGER) {
      throw new Error('[Paypercut Elements] The update revision limit has been reached.');
    }
    this.amount = amount;
    this.invalidateSubmissionValidation();
    this.updateRevision += 1;
    if (!this.controllerInitialized) return;

    const snapshot = this.getUpdateSnapshot();
    this.postControl({
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.UpdateOptions,
      controllerId: this.controllerId,
      updateRevision: snapshot.updateRevision,
      options: snapshot.options,
    } satisfies ElementsControllerUpdateOptionsMessage);
  }

  private startAppearancePreview(
    appearance: ElementsControllerOptions['appearance'],
    deferCommit = false,
  ): Promise<void | DeveloperAssistantPreparedAppearance> {
    const generation = ++this.appearancePreviewGeneration;
    const previous = cloneElementsAppearance(this.appearance);
    const candidate = cloneElementsAppearance(appearance);
    this.appearancePreview = { generation, appearance: candidate };
    this.invalidateSubmissionValidation();
    this.updateRevision += 1;

    if (this.controllerInitialized) {
      const snapshot = this.getUpdateSnapshot();
      this.postControl({
        channel: ELEMENTS_COMPONENT_CHANNEL,
        type: ElementsControllerCommandType.UpdateOptions,
        controllerId: this.controllerId,
        updateRevision: snapshot.updateRevision,
        options: snapshot.options,
      } satisfies ElementsControllerUpdateOptionsMessage);
    }

    return this.prepareMountedAppearanceTransitions(candidate).then(
      (transitions) => {
        if (this.appearancePreview?.generation !== generation) {
          transitions.forEach((transition) => transition.rollback());
          throw new AppearancePreviewError(
            'elements_appearance_preview_superseded',
            'A newer Appearance Preview replaced this request.',
          );
        }
        if (deferCommit) {
          return {
            isCurrent: () => this.appearancePreview?.generation === generation,
            commit: () => {
              transitions.forEach((transition) => transition.commit());
              this.commitAppearancePreview(generation, candidate);
            },
            rollback: async () => {
              transitions.forEach((transition) => transition.rollback());
              await this.restoreAppearancePreview(generation, previous);
            },
          } satisfies DeveloperAssistantPreparedAppearance;
        }
        transitions.forEach((transition) => transition.commit());
        this.commitAppearancePreview(generation, candidate);
        return undefined;
      },
      async (cause: unknown) => {
        if (this.appearancePreview?.generation !== generation) {
          throw new AppearancePreviewError(
            'elements_appearance_preview_superseded',
            'A newer Appearance Preview replaced this request.',
            cause,
          );
        }

        await this.restoreAppearancePreview(generation, previous);
        throw new AppearancePreviewError(
          'elements_appearance_preview_failed',
          'Appearance Preview failed; the last successfully applied Appearance remains active.',
          cause,
        );
      },
    );
  }

  private async prepareMountedAppearanceTransitions(
    appearance: ElementsControllerOptions['appearance'],
  ): Promise<PreparedAppearanceTransition[]> {
    const preparations: Array<Promise<PreparedAppearanceTransition>> = [];
    const cancellations: Array<(error: Error) => void> = [];
    if (this.hasMountedCardFields()) {
      for (const field of this.cardFields.values()) field.beginAppearanceTransition();
      preparations.push(this.cardCollectionRuntime.prepareAppearanceRemount(appearance));
      const generation = this.cardCollectionRuntime.getAppearanceRemountGeneration();
      cancellations.push((error) =>
        this.cardCollectionRuntime.cancelAppearanceRemount(generation, error),
      );
    }
    for (const card of this.getMountedCardElements()) {
      preparations.push(card.remountAppearance());
      const generation = card.componentGeneration;
      cancellations.push((error) => card.cancelAppearanceRemount(generation, error));
    }
    for (const express of this.getMountedExpressCheckoutElements()) {
      preparations.push(express.remountAppearance());
      const generation = express.componentGeneration;
      cancellations.push((error) => express.cancelAppearanceRemount(generation, error));
    }
    try {
      return await Promise.all(preparations);
    } catch (cause) {
      const cancellation = new Error('The coordinated Appearance Preview was cancelled.');
      cancellations.forEach((cancel) => cancel(cancellation));
      const results = await Promise.allSettled(preparations);
      for (const result of results) {
        if (result.status === 'fulfilled') result.value.rollback();
      }
      throw cause;
    }
  }

  private commitAppearancePreview(
    generation: number,
    candidate: ElementsControllerOptions['appearance'],
  ): void {
    if (this.appearancePreview?.generation !== generation) {
      throw new AppearancePreviewError(
        'elements_appearance_preview_superseded',
        'A newer Appearance Preview replaced this request.',
      );
    }
    this.appearance = cloneElementsAppearance(candidate);
    this.appearancePreview = null;
    for (const field of this.cardFields.values()) field.updateAppearance(this.appearance);
    this.cardCollectionRuntime.announceAppearanceCommit();
    for (const field of this.cardFields.values()) field.announceAppearanceReady();
    this.splitCardDeveloperAssistant?.refresh();
  }

  private async restoreAppearancePreview(
    generation: number,
    previous: ElementsControllerOptions['appearance'],
  ): Promise<void> {
    if (this.appearancePreview?.generation !== generation) return;
    this.appearancePreview = { generation, appearance: cloneElementsAppearance(previous) };
    this.updateRevision += 1;
    this.postAppearancePreviewOptions();
    try {
      const transitions = await this.prepareMountedAppearanceTransitions(previous);
      if (this.appearancePreview?.generation !== generation) {
        transitions.forEach((transition) => transition.rollback());
        return;
      }
      transitions.forEach((transition) => transition.commit());
    } catch (error) {
      if (this.appearancePreview?.generation === generation) {
        this.appearancePreview = null;
        for (const field of this.cardFields.values()) field.updateAppearance(this.appearance);
        this.cardCollectionRuntime.announceAppearanceCommit();
        for (const field of this.cardFields.values()) field.announceAppearanceReady();
        for (const card of this.getMountedCardElements()) card.markAppearanceRecoveryFailed();
        for (const express of this.getMountedExpressCheckoutElements()) {
          express.markAppearanceRecoveryFailed();
        }
        this.splitCardDeveloperAssistant?.refresh();
      }
      throw error;
    }
    if (this.appearancePreview?.generation !== generation) return;
    this.appearancePreview = null;
    for (const field of this.cardFields.values()) field.updateAppearance(this.appearance);
    this.cardCollectionRuntime.announceAppearanceCommit();
    for (const field of this.cardFields.values()) field.announceAppearanceReady();
    this.splitCardDeveloperAssistant?.refresh();
  }

  private postAppearancePreviewOptions(): void {
    if (!this.controllerInitialized) return;
    const snapshot = this.getUpdateSnapshot();
    this.postControl({
      channel: ELEMENTS_COMPONENT_CHANNEL,
      type: ElementsControllerCommandType.UpdateOptions,
      controllerId: this.controllerId,
      updateRevision: snapshot.updateRevision,
      options: snapshot.options,
    } satisfies ElementsControllerUpdateOptionsMessage);
  }

  private getMountedCardElements(): CardElementImpl[] {
    return Array.from(this.elements.values()).filter(
      (element): element is CardElementImpl =>
        element instanceof CardElementImpl && element.isMounted(),
    );
  }

  private getMountedExpressCheckoutElements(): ExpressCheckoutElementImpl[] {
    return Array.from(this.elements.values()).filter(
      (element): element is ExpressCheckoutElementImpl =>
        element instanceof ExpressCheckoutElementImpl && element.isMounted(),
    );
  }

  private clearAppearancePreviewFailures(): void {
    if (this.appearancePreview !== null) return;
    for (const card of this.getMountedCardElements()) card.clearAppearancePreviewFailure();
  }

  private hasMountedCardFields(): boolean {
    return Array.from(this.cardFields.values()).some((field) => field.isMounted());
  }

  private hasVisibleCardFields(): boolean {
    return Array.from(this.cardFields.values()).some((field) => field.isVisible());
  }

  private warnAppearancePreview(error: AppearancePreviewError): void {
    if (error.code === 'elements_appearance_preview_superseded') return;
    console.warn(`[Paypercut Elements] ${error.code}: ${error.message}`);
  }

  private appearancePreviewNotReadyError(): ElementsSubmitError {
    return createSubmitError(
      'elements_not_ready',
      'Payment details are refreshing their Appearance.',
      true,
    );
  }

  private warnAboutSkippedAppearance(): void {
    if (this.warnedAboutSkippedAppearance) return;
    this.warnedAboutSkippedAppearance = true;
    console.warn(
      '[Paypercut Elements] elements_appearance_locked: Appearance is locked after the first Element mounts. Configure Appearance during initialization.',
    );
  }

  isDestroyed(): boolean {
    return this.destroyed;
  }

  remove(element: ElementsComponentImpl): void {
    this.invalidateSubmissionValidation();
    if (element instanceof CardElementImpl) {
      this.paymentMethodSubmission.releaseSource(element.submissionSourceId);
    }
    this.detachComponent(element);
    this.elements.delete(element.componentId);
    if (element instanceof CardFieldElementImpl && this.cardFields.get(element.type) === element) {
      this.cardFields.delete(element.type);
      this.paymentMethodSubmission.releaseSource(
        this.cardCollectionSubmissionSource.submissionSourceId,
      );
      if (this.cardFields.size === 0) {
        this.unregisterSplitCardDeveloperAssistant();
      }
    }
  }

  detachComponent(element: ElementsComponentImpl): void {
    if (element instanceof CardFieldElementImpl) {
      this.cardCollectionRuntime.destroyField(element.componentId);
      return;
    }
    const pending = this.pendingComponents.get(element.componentId);
    if (pending) {
      pending.port.close();
      this.pendingComponents.delete(element.componentId);
    }
    if (this.controllerInitialized && this.controlPort) {
      const message: ElementsControllerDetachComponentMessage = {
        channel: ELEMENTS_COMPONENT_CHANNEL,
        type: ElementsControllerCommandType.DetachComponent,
        controllerId: this.controllerId,
        componentId: element.componentId,
        componentType: element.componentType,
      };
      try {
        this.controlPort.postMessage(message);
      } catch {
        // The controller may already be closing. Local teardown is still authoritative.
      }
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.paymentMethodSubmission.destroy();
    this.cardCollectionSubmissionSource.destroy();
    this.unregisterSplitCardDeveloperAssistant();
    for (const element of Array.from(this.elements.values())) element.destroy();
    this.elements.clear();
    this.cardFields.clear();
    this.cardCollectionRuntime.destroy();
    for (const pending of this.pendingComponents.values()) pending.port.close();
    this.pendingComponents.clear();
    this.walletAttemptOverlay.destroy();
    this.controlPort?.close();
    this.controlPort = null;
    this.controllerIframe?.remove();
    this.controllerIframe = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.windowMessageHandler);
    }
  }

  private ensureSplitCardDeveloperAssistant(): void {
    if (
      this.splitCardDeveloperAssistant ||
      this.clientOptions.developerAssistant !== true ||
      this.cardFields.size === 0 ||
      !this.cardCollectionRuntime.supportsDeveloperPresets() ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }
    this.splitCardDeveloperAssistant = registerDeveloperAssistantTarget({
      id: `${this.controllerId}:split-card-fields`,
      configurationIdentity: this.configurationIdentity,
      appearanceScopeId: this.controllerId,
      presetSource: 'split-card',
      isVisible: () => !this.destroyed && this.hasVisibleCardFields(),
      isAvailable: () => !this.destroyed && this.cardCollectionRuntime.canApplyDeveloperPreset(),
      isAppearanceAvailable: () => !this.destroyed && this.hasMountedCardFields(),
      appearancePreviewClearsTestData: () => true,
      canPreview: () => this.isAppearancePreviewAvailable(),
      applyCardValue: (value) => this.cardCollectionRuntime.applyDeveloperPreset(value),
      getAppearance: () => this.getAppearance(),
      updateAppearance: (appearance) => this.updateAppearance(appearance),
      replaceAppearance: (appearance) => this.replaceAppearance(appearance),
      prepareAppearance: (appearance) => this.prepareAppearance(appearance),
      prepareAppearanceReplacement: (appearance) =>
        this.prepareAppearanceReplacement(appearance),
    });
  }

  private unregisterSplitCardDeveloperAssistant(): void {
    this.splitCardDeveloperAssistant?.unregister();
    this.splitCardDeveloperAssistant = null;
  }

  private onWindowMessage(event: MessageEvent): void {
    if (
      this.destroyed ||
      this.controllerBootstrapped ||
      !this.controllerIframe?.contentWindow ||
      event.source !== this.controllerIframe.contentWindow ||
      event.origin !== this.hostedOrigin ||
      !isElementsControllerFrameReadyMessage(event.data)
    ) {
      return;
    }

    this.controllerBootstrapped = true;
    const channel = new MessageChannel();
    this.controlPort = channel.port1;
    this.controlPort.onmessage = (controlEvent) => this.onControlMessage(controlEvent);
    this.controlPort.start();

    const message: ElementsControllerInitMessage = {
      channel: ELEMENTS_CONTROLLER_CHANNEL,
      type: ElementsControllerMessageType.Init,
      controllerId: this.controllerId,
      payload: {
        publishableKey: this.clientOptions.publishableKey,
        apiUrl: getApiOrigin(this.clientOptions.apiUrl),
        sdkVersion: config.version,
        options: this.getUpdateSnapshot().options,
      },
    };
    this.controllerIframe.contentWindow.postMessage(message, this.hostedOrigin, [channel.port2]);
  }

  private onControlMessage(event: MessageEvent): void {
    if (this.destroyed) {
      event.ports.forEach((port) => port.close());
      return;
    }
    if (isElementsControllerInitializedMessage(event.data)) {
      if (event.data.controllerId !== this.controllerId) return;
      this.cardCollectionRuntime.configure(event.data.payload.cardCollection ?? null);
      this.ensureSplitCardDeveloperAssistant();
      this.controllerInitialized = true;
      this.flushPendingComponents();
      if (this.updateRevision > 0) {
        const snapshot = this.getUpdateSnapshot();
        const message: ElementsControllerUpdateOptionsMessage = {
          channel: ELEMENTS_COMPONENT_CHANNEL,
          type: ElementsControllerCommandType.UpdateOptions,
          controllerId: this.controllerId,
          updateRevision: snapshot.updateRevision,
          options: snapshot.options,
        };
        this.postControl(message);
      }
      return;
    }

    if (isElementsControllerErrorMessage(event.data)) {
      if (event.data.controllerId !== this.controllerId) return;
      this.cardCollectionRuntime.fail(event.data.payload);
      this.paymentMethodSubmission.handleSourceError(
        this.cardCollectionSubmissionSource.submissionSourceId,
        event.data.payload,
      );
      for (const element of this.elements.values()) {
        this.paymentMethodSubmission.handleSourceError(
          element instanceof CardElementImpl ? element.submissionSourceId : element.componentId,
          event.data.payload,
        );
        element.handleControllerError(event.data.payload);
      }
      return;
    }

    if (!isElementsComponentEventMessage(event.data)) return;
    const message: ElementsComponentEventMessage = event.data;
    if (message.controllerId !== this.controllerId) return;
    if (event.ports.length !== 0) {
      event.ports.forEach((port) => port.close());
      return;
    }
    if (message.componentType === 'card_collection') {
      if (message.componentId !== this.cardCollectionSubmissionSource.submissionSourceId) return;
      if (message.payload.event === 'tokenization_requested') {
        if (
          !this.paymentMethodSubmission.ownsAttempt(message.componentId, message.payload.requestId)
        ) {
          return;
        }
        this.cardCollectionSubmissionSource.handleTokenizationRequest(
          message.payload.requestId,
          message.payload.setupFutureUsage,
        );
      }
      this.paymentMethodSubmission.handleSourceEvent(message.componentId, message.payload);
      return;
    }
    const element = this.elements.get(message.componentId);
    if (!element || message.componentType !== element.componentType) {
      event.ports.forEach((port) => port.close());
      return;
    }
    if ((message.componentGeneration ?? 0) !== element.componentGeneration) {
      return;
    }
    this.paymentMethodSubmission.handleSourceEvent(
      element instanceof CardElementImpl ? element.submissionSourceId : message.componentId,
      message.payload,
    );
    element.handleControllerEvent(message.payload);
  }

  private flushPendingComponents(): void {
    if (!this.controllerInitialized || !this.controlPort) return;
    for (const [componentId, pending] of this.pendingComponents) {
      const message: ElementsControllerAttachComponentMessage = {
        channel: ELEMENTS_COMPONENT_CHANNEL,
        type: ElementsControllerCommandType.AttachComponent,
        controllerId: this.controllerId,
        componentId,
        componentType: pending.element.componentType,
        componentOptions: pending.element.getComponentOptions(),
        ...(pending.element.componentGeneration > 0
          ? { componentGeneration: pending.element.componentGeneration }
          : {}),
      };
      try {
        this.controlPort.postMessage(message, [pending.port]);
        this.pendingComponents.delete(componentId);
      } catch {
        pending.element.handleControllerError({
          code: 'elements_component_attach_failed',
          message: 'The secure payment form could not be attached.',
          recoverable: true,
        });
      }
    }
  }

  private postControl(message: object, transfer: Transferable[] = []): void {
    if (!this.controllerInitialized || !this.controlPort) {
      throw new Error('[Paypercut Elements] The payment controller is not ready.');
    }
    if (transfer.length > 0) this.controlPort.postMessage(message, transfer);
    else this.controlPort.postMessage(message);
  }
}

const UPDATE_FIELDS = new Set(['amount', 'appearance']);

function isSubmitErrorWithCode(
  error: unknown,
  code: ElementsErrorCode,
): error is ElementsSubmitError {
  return isRecord(error) && error.code === code;
}

function normalizeUpdateOptions(
  options: PaypercutElementsUpdateOptions,
  currentAppearance: ElementsControllerOptions['appearance'],
  currentAmount = 0,
): {
  amount: number;
  appearance: ElementsControllerOptions['appearance'];
} {
  if (
    !isRecord(options) ||
    Object.keys(options).length === 0 ||
    Object.keys(options).some((key) => !UPDATE_FIELDS.has(key))
  ) {
    throw new Error(
      '[Paypercut Elements] Update options may contain only amount or appearance details.',
    );
  }
  const requestedAppearance = (options as PaypercutElementsUpdateOptions).appearance;
  const requestedAmount = (options as PaypercutElementsUpdateOptions).amount;

  if (
    requestedAmount !== undefined &&
    (!Number.isSafeInteger(requestedAmount) || requestedAmount < 0)
  ) {
    throw new Error('[Paypercut Elements] Updated amount must be a non-negative safe integer.');
  }

  return {
    amount: requestedAmount ?? currentAmount,
    appearance:
      requestedAppearance === undefined
        ? currentAppearance
        : normalizeAppearanceUpdate(requestedAppearance, currentAppearance),
  };
}

function normalizeAppearanceUpdate(
  update: PaypercutElementsAppearance,
  current: ElementsControllerOptions['appearance'],
): ElementsControllerOptions['appearance'] {
  assertValidElementsAppearance(update);

  const rules = { ...current?.rules };
  for (const [selector, declarations] of Object.entries(update.rules ?? {})) {
    rules[selector as keyof typeof rules] = {
      ...rules[selector as keyof typeof rules],
      ...declarations,
    };
  }

  return cloneElementsAppearance({
    ...current,
    ...update,
    ...(current?.variables || update.variables
      ? { variables: { ...current?.variables, ...update.variables } }
      : {}),
    ...(Object.keys(rules).length > 0 ? { rules } : {}),
  });
}

function toControllerOptions(
  options: PaypercutElementsOptions,
  amount: number,
  currentAppearance: ElementsControllerOptions['appearance'],
): ElementsControllerOptions {
  const appearance = cloneElementsAppearance(currentAppearance);
  return {
    mode: options.mode,
    amount,
    currency: options.currency,
    ...(options.setupFutureUsage ? { setupFutureUsage: options.setupFutureUsage } : {}),
    ...(options.locale ? { locale: requestedLocaleForController(options.locale) } : {}),
    ...(appearance ? { appearance } : {}),
  };
}

const REQUESTED_LOCALE_MAX_CHARACTERS = 35;

function requestedLocaleForController(locale: string): string {
  const requested = String(locale);
  return requested.length <= REQUESTED_LOCALE_MAX_CHARACTERS &&
    !containsAsciiControlCharacter(requested)
    ? requested
    : 'auto';
}

function resolveHostedOrigin(override?: string): string {
  try {
    return new URL(getCheckoutOrigin(override)).origin;
  } catch {
    throw new Error('[Paypercut Elements] The configured hosted checkout URL is invalid.');
  }
}
